import { useEffect, useRef, useState } from "react";
import { SongList } from "../../spotipitan/song-list";
import { getPlaylistPosterUrl, Spotip17anFunctions } from "../../../../utils/api";
import { ArtistResult, PlaylistResult, SearchCatalogMode, Song } from "../../spotipitan/types";
import ItemsHorizontalScroll from "../../../squadDiskShared/itemsHorizontalScroll/itemsHorizontalScroll";
import ItemDetailsToView from "../../../squadDiskShared/itemDetailsView/itemDetailsToView";
import ImageMosaic from "../../../shared/image-mosaic/image-mosaic";
import { useNavigate } from "react-router-dom";
import { ScrollModal } from "../../../shared/scroll-modal";
import { ScrollModalApi } from "../../../shared/scroll-modal/view";
import LocalStorage from "../../../../utils/local-storage";

const SpotipitanSearchResults = () => {
    // get q from query
    const searchQuery = new URLSearchParams(window.location.search).get('q') || '';
    const [searchResults, setSearchResults] = useState<Song[]>([]);
    const [queryResults, setQueryResults] = useState<Song[]>([]);
    const [playlists, setPlaylists] = useState<PlaylistResult[]>([]);
    const [artists, setArtists] = useState<ArtistResult[]>([]);
    const [loading, setLoading] = useState(true);
    const [searchMode, setSearchMode] = useState<SearchCatalogMode>(
        () => LocalStorage.getItem<SearchCatalogMode>('search_catalog_mode') || 'deezer'
    );
    const modalRef = useRef<ScrollModalApi>(null);
    const navigate = useNavigate();

    const handleSelectMode = (mode: SearchCatalogMode) => {
        setSearchMode(mode);
        LocalStorage.setItem('search_catalog_mode', mode);
        modalRef.current?.close();
    };

    useEffect(() => {
        setLoading(true);
        setPlaylists([]);
        setArtists([]);
        setSearchResults([]);
        setQueryResults([]);

        Spotip17anFunctions.use('ytdbSearch').call({ query: searchQuery, searchMode }).then(async (res: {
            savedSongs: Song[],
            videos: Song[],
            playlists: PlaylistResult[],
            artists: ArtistResult[]
        }) => {
            setSearchResults(res.videos || []);
            setQueryResults(res.savedSongs || []);

            const playlistsPromise = (res.playlists || []).map(async (playlist) => {
                const posterUrl = await getPlaylistPosterUrl(playlist);
                return {
                    ...playlist,
                    poster_url: posterUrl || playlist.poster_url
                };
            });

            const artistsPromise = (res.artists || []).map(async (artist) => {
                const posterUrl = await getPlaylistPosterUrl(artist);
                return {
                    ...artist,
                    poster_url: posterUrl || artist.poster_url
                };
            });

            setPlaylists(await Promise.all(playlistsPromise));
            setArtists(await Promise.all(artistsPromise));
            setLoading(false);
        }).catch(err => {
            console.error("Error in search:", err);
            setLoading(false);
        });

    }, [searchQuery, searchMode]);

    const getBadgeConfig = () => {
        switch (searchMode) {
            case 'music':
                return {
                    color: "var(--color-primary, #1db954)",
                    icon: "fa-solid fa-music me-1",
                    label: "YouTube Music",
                    title: "Catálogo YouTube Music: Solo música oficial"
                };
            case 'youtube':
                return {
                    color: "#ff4444",
                    icon: "fa-brands fa-youtube me-1",
                    label: "Todo YouTube",
                    title: "Catálogo completo de YouTube"
                };
            case 'deezer':
            default:
                return {
                    color: "#c04dff",
                    icon: "fa-solid fa-record-vinyl me-1",
                    label: "Deezer (Estudio)",
                    title: "Catálogo Deezer: Versiones oficiales de estudio y portadas HD"
                };
        }
    };

    const badgeConfig = getBadgeConfig();

    return (
        <div className={"container"}>
            <div className="d-flex align-items-center justify-content-between flex-wrap gap-2 mb-4">
                <div className="d-flex align-items-center gap-2 flex-wrap">
                    <h2 className="mb-0">Resultados para "{searchQuery}"</h2>
                    <button
                        className="badge rounded-pill btn btn-sm d-inline-flex align-items-center"
                        style={{
                            fontSize: "0.8rem",
                            padding: "6px 14px",
                            backgroundColor: "rgba(255, 255, 255, 0.08)",
                            border: "1px solid rgba(255, 255, 255, 0.2)",
                            color: badgeConfig.color,
                            cursor: "pointer",
                            transition: "all 0.2s ease"
                        }}
                        onClick={(e) => {
                            modalRef.current?.open({ x: e.clientX, y: e.clientY });
                        }}
                        title={badgeConfig.title}
                    >
                        <i className={badgeConfig.icon}></i>
                        {badgeConfig.label}
                    </button>
                </div>
            </div>


            <ScrollModal
                ref={modalRef}
                header={
                    <div className="d-flex align-items-center gap-2 fw-bold fs-5 px-3 py-2">
                        <i className="fa-solid fa-sliders"></i>
                        <span>Filtro de Catálogo</span>
                    </div>
                }
            >
                <div className="p-3 d-flex flex-column gap-3">
                    <p className="text-muted mb-1" style={{ fontSize: "0.9rem" }}>
                        Elige el motor de catálogo que se usará para buscar canciones y vídeos:
                    </p>

                    {/* Opción Deezer */}
                    <div
                        className="p-3 rounded d-flex align-items-start gap-3"
                        style={{
                            backgroundColor: searchMode === 'deezer' ? 'rgba(192, 77, 255, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                            border: searchMode === 'deezer' ? '1px solid #c04dff' : '1px solid rgba(255, 255, 255, 0.1)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onClick={() => handleSelectMode('deezer')}
                    >
                        <div className="mt-1">
                            <i className="fa-solid fa-record-vinyl fs-4" style={{ color: '#c04dff' }}></i>
                        </div>
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <strong className="text-white">Catálogo Deezer (Estudio)</strong>
                                {searchMode === 'deezer' && (
                                    <i className="fa-solid fa-circle-check text-success fs-5"></i>
                                )}
                            </div>
                            <div className="text-muted" style={{ fontSize: "0.85rem", lineHeight: "1.3" }}>
                                Recomendado por defecto. Catálogo oficial de estudio de Deezer. Portadas en alta fidelidad y álbumes completos con audio enlazado para reproducción.
                            </div>
                        </div>
                    </div>

                    {/* Opción YouTube Music */}
                    <div
                        className="p-3 rounded d-flex align-items-start gap-3"
                        style={{
                            backgroundColor: searchMode === 'music' ? 'rgba(var(--color-primary-rgb, 29, 185, 84), 0.15)' : 'rgba(255, 255, 255, 0.05)',
                            border: searchMode === 'music' ? '1px solid var(--color-primary, #1db954)' : '1px solid rgba(255, 255, 255, 0.1)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onClick={() => handleSelectMode('music')}
                    >
                        <div className="mt-1">
                            <i className="fa-solid fa-music fs-4 text-success"></i>
                        </div>
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <strong className="text-white">Solo Música (YouTube Music)</strong>
                                {searchMode === 'music' && (
                                    <i className="fa-solid fa-circle-check text-success fs-5"></i>
                                )}
                            </div>
                            <div className="text-muted" style={{ fontSize: "0.85rem", lineHeight: "1.3" }}>
                                Filtra exclusivamente temas oficiales, álbumes y artistas de YouTube Music.
                            </div>
                        </div>
                    </div>

                    {/* Opción YouTube General */}
                    <div
                        className="p-3 rounded d-flex align-items-start gap-3"
                        style={{
                            backgroundColor: searchMode === 'youtube' ? 'rgba(255, 0, 0, 0.15)' : 'rgba(255, 255, 255, 0.05)',
                            border: searchMode === 'youtube' ? '1px solid #ff4444' : '1px solid rgba(255, 255, 255, 0.1)',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease'
                        }}
                        onClick={() => handleSelectMode('youtube')}
                    >
                        <div className="mt-1">
                            <i className="fa-brands fa-youtube fs-4 text-danger"></i>
                        </div>
                        <div className="flex-grow-1">
                            <div className="d-flex align-items-center justify-content-between mb-1">
                                <strong className="text-white">Todo YouTube (General)</strong>
                                {searchMode === 'youtube' && (
                                    <i className="fa-solid fa-circle-check text-success fs-5"></i>
                                )}
                            </div>
                            <div className="text-muted" style={{ fontSize: "0.85rem", lineHeight: "1.3" }}>
                                Catálogo completo de YouTube. Incluye videoclips, directos, covers, tutoriales y canales de YouTube.
                            </div>
                        </div>
                    </div>
                </div>
            </ScrollModal>


            {loading && (
                <div className="d-flex justify-content-center align-items-center py-5">
                    <div className="spinner-border text-primary" role="status">
                        <span className="visually-hidden">Cargando...</span>
                    </div>
                </div>
            )}

            {!loading && !artists.length && !playlists.length && !queryResults.length && !searchResults.length && (
                <div className="text-center py-5 text-muted">
                    <i className="fa-solid fa-magnifying-glass fs-1 mb-3"></i>
                    <p>No se encontraron resultados para "{searchQuery}"</p>
                </div>
            )}

            {/* ARTISTAS */}
            {!!artists?.length && (
                <div className="mb-4">
                    <h3 className="h5 mb-2 text-muted fw-bold d-flex align-items-center gap-2">
                        <i className="fa-solid fa-microphone"></i> Artistas
                    </h3>
                    <div style={{ height: '170px' }}>
                        <ItemsHorizontalScroll className="container spotipitan-home__items-container">
                            {artists.map((artist) => {
                                const isNative = !!artist.is_native || (!!artist.spotip17an_artist && typeof artist.id === 'number');
                                const desc = artist.songs_count
                                    ? `${artist.songs_count} canciones`
                                    : (artist.subscribers || (isNative ? "Artista Spotip17an" : "Artista"));

                                const artistImg = artist.poster_url ? (
                                    <img
                                        src={artist.poster_url}
                                        alt={artist.name || artist.username}
                                        style={{
                                            width: "130px",
                                            height: "130px",
                                            objectFit: "cover",
                                            borderRadius: "50%",
                                            boxShadow: isNative ? "0 0 10px rgba(var(--color-primary-rgb), 0.6)" : "none"
                                        }}
                                    />
                                ) : (
                                    <ImageMosaic images={(artist.songs || []).map(x => x.poster_url)} />
                                );

                                return (
                                    <ItemDetailsToView
                                        key={artist.id}
                                        tag={isNative ? "Spotip17an" : undefined}
                                        description={desc}
                                        title={artist.name || artist.username}
                                        onClick={() => navigate(`/artist/${artist.id}`)}
                                        showHover={true}
                                        iconClass=""
                                        image={artistImg}
                                    />
                                );
                            })}
                        </ItemsHorizontalScroll>
                    </div>
                </div>
            )}

            {/* PLAYLISTS Y ÁLBUMES */}
            {!!playlists?.length && (
                <div className="mb-4">
                    <h3 className="h5 mb-2 text-muted fw-bold d-flex align-items-center gap-2">
                        <i className="fa-solid fa-record-vinyl"></i> Playlists y Álbumes
                    </h3>
                    <div style={{ height: '170px' }}>
                        <ItemsHorizontalScroll className="container spotipitan-home__items-container">
                            {playlists.map((playlist) => {
                                const isNative = !!playlist.is_native || (typeof playlist.id === 'number' && !!playlist.creator_uid);
                                const desc = playlist.songs_count
                                    ? `${playlist.songs_count} canciones`
                                    : (playlist.creator_name || (isNative ? "Playlist Spotip17an" : "Playlist"));

                                return (
                                    <ItemDetailsToView
                                        key={playlist.id}
                                        tag={isNative ? "Spotip17an" : undefined}
                                        description={desc}
                                        title={playlist.name || playlist.username}
                                        onClick={() => navigate(`/playlist/${playlist.id}`)}
                                        showHover={true}
                                        iconClass=""
                                        image={playlist?.poster_url ||
                                            <ImageMosaic images={((playlist.songs as any[]) || []).map(x => x.poster_url || x)} />}
                                    />
                                );
                            })}
                        </ItemsHorizontalScroll>
                    </div>
                </div>
            )}

            {/* CANCIONES DE LA COMUNIDAD */}
            {!!queryResults?.length && !loading && (
                <div className="mb-3">
                    <h3 className="h5 mb-2 text-muted fw-bold d-flex align-items-center gap-2">
                        <i className="fa-solid fa-star text-warning"></i> Temas de Spotip17an
                    </h3>
                    <SongList songs={queryResults} loadingSongs={loading} canScrollToQueue
                        style={{ paddingBottom: "0.5rem" }} markDownloaded />
                </div>
            )}

            {/* CANCIONES OFICIALES */}
            {!!searchResults?.length && !loading && (
                <div className="mb-3">
                    {!!queryResults?.length && (
                        <h3 className="h5 mb-2 mt-4 text-muted fw-bold d-flex align-items-center gap-2">
                            <i className="fa-solid fa-music"></i> Canciones
                        </h3>
                    )}
                    <SongList songs={searchResults} loadingSongs={loading} canScrollToQueue markDownloaded />
                </div>
            )}
        </div>
    );
};

export default SpotipitanSearchResults;

